import React, { useContext, useEffect, memo, useMemo } from "react";
import { LazyLoadComponent } from "react-lazy-load-image-component";


import { useTranslations } from "../hooks/use-translations";

import { GenericCollectionRowTitle, CollectionRow } from "./components/row-section.js";
import { CallToAction } from "../components/callToAction";
import { store } from "../components/Store";
import { Titles } from "../components/Titles";
import { SkeletonBooks } from "./components/skeletonBooks.js";

import { FilterBooks } from "../components/FilterBooks/filterBooks.js";
import { useUserHomeData } from "../hooks/home/use-user-home.js";

import { Helmet } from "react-helmet";
import { useLanguage } from "../hooks/use-language.js";
import { useTranslationsById } from "../hooks/use-translations-by-id.js";
import { useCuradurias } from "../hooks/use-curadurias.js";
import { Countries } from "./components/countries.js";
import { DescriptiveRowBooks } from "./components/descriptiveRowBooks.js";
import { DescriptiveRowPublishers } from "./components/descriptiveRowPublishers.js";
import { usePublishersBooksData } from "./hooks/use-publishers-books.js";
import { AwardAasl1 } from "../components/awards/award-aasl.js";


/**
 * Componente que muestra la página de cátalogo de libros.
 * @param {Object} props - Props del componente.
 * @param {Object} props.data - Datos de la página de inicio del usuario.
 * @returns {JSX.Element} Elemento JSX que representa la página de inicio del usuario.
 */
export const Libros = memo(() => {
    const lang = useLanguage();
    const textosHome = useTranslations({ section: "home", lang: lang });
    const txts = useTranslationsById({ mainSectionName: "libros", language: lang });
    const homeData = useUserHomeData();

    const textos = textosHome?.sin_login || {};
    //const homeData = data?.data;
    const globalState = useContext(store);

    const loading = globalState?.state?.userBooksData?.loading && globalState?.state?.userBooksData?.loadCompleted === false && globalState?.state?.cantidadLibros === -1;

    const curadurias = useCuradurias();
    const paises = useMemo(() => {
        return Array.isArray(curadurias?.paises) ? curadurias?.paises : []
    }, [curadurias?.paises]);

    const publishersData = usePublishersBooksData();
    const editoriales = useMemo(() => {
        return Array.isArray(publishersData.publishers) ? publishersData.publishers : []
    }, [publishersData.publishers]);

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])


    return (
        <>
            <Helmet>
                <title>{txts?.getText({ id: "metadata.title", defaultText: "MakeMake - Libros" })}</title>
                <meta name="description" content={txts?.getText({ id: "metadata.description", defaultText: "Explora el catálogo de MakeMake, una colección de libros narrativos e informativos para niños y jóvenes de altísima calidad." })} />
            </Helmet>

            {
                !loading && homeData?.fetching ?
                    //<SkeletonHomeCollection />
                    <SkeletonBooks />
                    :

                    <div className="section-libros">
                        <AwardAasl1 page={"landing"}/>
                        <Titles
                            hexBackground="#6666CC"
                            color="onsecondary--color"
                            title={txts?.getText({ id: "title", defaultText: "Conoce la colección" })}
                        />


                        <LazyLoadComponent threshold={.5}>

                            <FilterBooks />

                            <CollectionRow
                                containerProps={{
                                    className: 'book-row-yellow'
                                }}
                                titleElement={
                                    <GenericCollectionRowTitle text={txts?.getText({ id: "interactive_books.title", defaultText: "Libros Interactivos" })} />
                                }
                            >
                                <DescriptiveRowBooks txtsInterfaz={txts} text={txts?.getText({ id: "interactive_books.description", defaultText: "Los libros interactivos incorporan animación, lectura en voz alta, música y algunas actividades lúdicas. Son ideales para atraer la atención de lectores con poca familiaridad con el libro impreso. ¡Exclusivos en MakeMake!" })} books={homeData?.data?.LibrosInteractivos} />
                            </CollectionRow>

                            <CollectionRow
                                containerProps={{
                                    className: 'book-row-violet'
                                }}
                                titleSpecialElement={
                                    <GenericCollectionRowTitle text={txts.getText({ id: "publishers.title", defaultText: "En MakeMake encuentras las mejores editoriales de América Latina" })} />
                                }
                            >
                                <DescriptiveRowPublishers text={txts.getText({ id: "publishers.description", defaultText: "Las mejores editoriales de América Latina aportan a MakeMake lo mejor de su catálogo para brindar a los lectores una colección de ficción y de no ficción diversa, interesante y en contaste actualización." })} publishers={editoriales} />
                            </CollectionRow>

                            <CollectionRow
                                titleElement={
                                    <GenericCollectionRowTitle text={txts?.getText({ id: "countries.title", defaultText: "Una colección diversa" })} />
                                }
                            >
                                <Countries
                                    text={textos?.paises?.description || "Agrupaciones temáticas de libros elegidas por nuestros expertos en lectura."}
                                    category="paises"
                                    group={paises}
                                />
                            </CollectionRow>

                            <CallToAction text={txts?.getText({ id: "demo.title", defaultText: "¿Quieres que estos libros estén disponibles 24/7 en tu biblioteca público o en tu colegio?" })} />


                            <CollectionRow
                                containerProps={{
                                    className: 'home-row-yellow'
                                }}
                                titleElement={
                                    <GenericCollectionRowTitle text={txts?.getText({ id: "recommended.title", defaultText: "Nuestros libros recomendados" })} />
                                }>
                                <DescriptiveRowBooks  txtsInterfaz={txts}  text={txts?.getText({ id: "recommended.description", defaultText: "Todos las semanas te recomendamos libros de nuestra colección. Adicionalmente tenemos listas de lectura temáticas, autores destacados y muchas más herramientas de promoción para incentivar una lectura de calidad en colegios y bibliotecas." })} books={homeData?.data?.librosRecomendados} />
                            </CollectionRow>


                        </LazyLoadComponent>

                    </div>
            }
        </>

    )
})